import React, { useState, Component } from 'react';
import { Star, Check, X, ChevronDown, ChevronUp, Menu, ShoppingCart, AlertCircle, ArrowRight, ShieldCheck, Truck, Clock } from 'lucide-react';
// --- Sub-components ---
const RatingBadge = () => <div className="bg-white shadow-lg rounded-lg p-3 flex flex-col items-center border border-gray-100 absolute top-4 right-4 z-10 hidden md:flex" data-id="element-1">
    <div className="flex items-center space-x-1 mb-1" data-id="element-2">
      <span className="font-bold text-2xl text-gray-900" data-id="element-3">4.9</span>
      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" data-id="element-4" />
    </div>
    <div className="text-xs text-gray-500 font-medium" data-id="element-5">
      Based on 12,000+ reviews
    </div>
    <div className="w-full h-1 bg-gray-100 mt-2 rounded-full overflow-hidden" data-id="element-6">
      <div className="w-[98%] h-full bg-green-500" data-id="element-7"></div>
    </div>
  </div>;
const HighlightBox = ({
  children,
  variant = 'yellow'
}: {
  children: React.ReactNode;
  variant?: 'yellow' | 'lime' | 'blue';
}) => {
  const colors = {
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-900',
    lime: 'bg-lime-50 border-lime-200 text-lime-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900'
  };
  return <div className={`p-6 rounded-xl border ${colors[variant]} my-8 text-lg leading-relaxed`} data-id="element-8">
      {children}
    </div>;
};
const BeforeAfter = ({
  label1 = 'BEFORE',
  label2 = 'AFTER'
}) => <div className="flex flex-col sm:flex-row gap-2 my-8 rounded-xl overflow-hidden shadow-md" data-id="element-9">
    <div className="relative flex-1 h-32 bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center" data-id="element-10">
      <div className="text-center" data-id="element-11">
        <span className="text-4xl mb-2 block" data-id="element-12">😣</span>
        <p className="text-red-800 font-medium text-sm" data-id="element-13">Stiff & Swollen</p>
      </div>
      <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded shadow-sm" data-id="element-14">
        {label1}
      </span>
    </div>
    <div className="relative flex-1 h-32 bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center" data-id="element-15">
      <div className="text-center" data-id="element-16">
        <span className="text-4xl mb-2 block" data-id="element-17">😊</span>
        <p className="text-green-800 font-medium text-sm" data-id="element-18">Flexible & Healthy</p>
      </div>
      <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded shadow-sm" data-id="element-19">
        {label2}
      </span>
    </div>
  </div>;
const CTAButton = ({
  text = 'TRY BARKVEE RISK-FREE TODAY',
  subtext
}: {
  text?: string;
  subtext?: string;
}) => <div className="flex flex-col items-center w-full my-8" data-id="element-20">
    <a href="https://barkvee.com/products/thermaflex" className="w-full bg-green-500 hover:bg-green-600 text-white font-bold text-xl py-5 px-6 rounded-xl shadow-lg transform transition hover:-translate-y-1 active:translate-y-0 flex items-center justify-center group text-center no-underline" data-id="element-21">
      <span data-id="element-22">{text}</span>
      <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-23" />
    </a>
    {subtext && <p className="mt-3 text-sm text-gray-500" data-id="element-24">{subtext}</p>}
    <div className="flex items-center justify-center space-x-4 mt-3 text-xs text-gray-500" data-id="element-25">
      <span className="flex items-center" data-id="element-26">
        <ShieldCheck className="w-3 h-3 mr-1" data-id="element-27" /> 60-Day Guarantee
      </span>
      <span className="flex items-center" data-id="element-28">
        <Truck className="w-3 h-3 mr-1" data-id="element-29" /> Free Shipping
      </span>
    </div>
  </div>;
const CheckListItem = ({
  children
}: {
  children: React.ReactNode;
}) => <li className="flex items-start space-x-3 mb-3" data-id="element-30">
    <div className="flex-shrink-0 mt-1" data-id="element-31">
      <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center" data-id="element-32">
        <Check className="w-3 h-3 text-green-600" data-id="element-33" />
      </div>
    </div>
    <span className="text-gray-700 text-lg" data-id="element-34">{children}</span>
  </li>;
const CrossListItem = ({
  children
}: {
  children: React.ReactNode;
}) => <li className="flex items-start space-x-3 mb-3" data-id="element-35">
    <div className="flex-shrink-0 mt-1" data-id="element-36">
      <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center" data-id="element-37">
        <X className="w-3 h-3 text-red-600" data-id="element-38" />
      </div>
    </div>
    <span className="text-gray-700 text-lg" data-id="element-39">{children}</span>
  </li>;
const TestimonialCard = ({
  quote,
  author,
  role,
  imageUrl
}: {
  quote: string;
  author: string;
  role?: string;
  imageUrl: string;
}) => <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 my-6" data-id="element-40">
    <div className="flex items-start gap-4" data-id="element-41">
      <img src={imageUrl} alt={author} className="w-12 h-12 rounded-full object-cover flex-shrink-0" data-id="element-42" />
      <div className="flex-1" data-id="element-43">
        <div className="flex space-x-1 mb-3" data-id="element-44">
          {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-45" />)}
        </div>
        <p className="text-gray-800 italic mb-4 text-lg" data-id="element-46">"{quote}"</p>
        <div data-id="element-47">
          <p className="font-bold text-gray-900" data-id="element-48">{author}</p>
          {role && <p className="text-sm text-gray-500" data-id="element-49">{role}</p>}
        </div>
      </div>
    </div>
  </div>;
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-0" data-id="element-50">
      <button className="w-full py-4 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-51">
        <span className="font-bold text-gray-900 text-lg pr-4" data-id="element-52">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-53" /> : <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-54" />}
      </button>
      {isOpen && <div className="pb-6 text-gray-600 leading-relaxed" data-id="element-55">{answer}</div>}
    </div>;
};
// --- Main Page Component ---
export function ListiclePage() {
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-56">
      {/* Navigation / Header */}
      <nav className="border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur-sm z-50" data-id="element-57">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between" data-id="element-58">
          <div className="flex items-center space-x-2" data-id="element-59">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold" data-id="element-60">
              B
            </div>
            <span className="font-bold text-xl tracking-tight" data-id="element-61">Barkvee</span>
          </div>
          <div className="flex items-center" data-id="element-62">
            <a href="https://barkvee.com/products/thermaflex" className="bg-green-500 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-green-600 transition-colors no-underline" data-id="element-63">
              Shop Now
            </a>
          </div>
        </div>
      </nav>

      <main className="max-w-[680px] mx-auto px-4 py-8 sm:py-12" data-id="element-64">
        {/* Hero Section */}
        <header className="mb-12 text-center sm:text-left relative" data-id="element-65">
          <span className="inline-block bg-blue-50 text-blue-700 text-xs font-bold px-3 py-1 rounded-full mb-4 tracking-wide uppercase" data-id="element-66">
            Morning Stiffness Relief
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6" data-id="element-67">
            5 Reasons Why Your Ankle Pain Gets Worse in the Morning (And What
            Actually Helps)
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-68">
            If your first steps feel like walking on broken glass, here's what's
            actually happening to your ankle while you sleep—and the surprising
            solution that's helping thousands loosen up faster.
          </p>

          <div className="relative rounded-2xl overflow-hidden mb-8" data-id="element-69">
            <img src="https://cdn.magicpatterns.com/uploads/3c3J8on3N34aL36HwHFexF/hf_20260202_150832_46a5b980-ccdc-4c03-b744-d66c5ee19071.png" alt="Barkvee Heated Ankle Wrap" className="w-full h-auto object-cover" data-id="element-70" />
            <RatingBadge data-id="element-71" />
          </div>

          <div className="flex items-center justify-center sm:justify-start space-x-2 text-sm text-gray-500 mb-8 border-b border-gray-100 pb-8" data-id="element-72">
            <div className="flex -space-x-2" data-id="element-73">
              {['https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face', 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face', 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face'].map((url, i) => <img key={i} src={url} alt="Customer" className="w-8 h-8 rounded-full border-2 border-white object-cover" data-id="element-74" />)}
            </div>
            <span data-id="element-75">Read by 45,000+ people this month</span>
          </div>
        </header>

        {/* Introduction Narrative */}
        <section className="prose prose-lg prose-blue max-w-none text-gray-700 space-y-6" data-id="element-76">
          <p className="font-bold text-xl text-gray-900" data-id="element-77">
            You know the routine.
          </p>
          <p data-id="element-78">
            Your alarm goes off. You sit up in bed. You swing your legs over the
            side.
          </p>
          <p data-id="element-79">
            And the moment your foot touches the floor—sharp, stiff, shooting
            pain.
          </p>
          <p data-id="element-80">Your ankle feels frozen. Tight. Like it hasn't moved in weeks.</p>
          <p data-id="element-81">
            You hobble to the bathroom like the Tin Man, taking slow, careful
            steps, waiting for it to "warm up."
          </p>
          <p data-id="element-82">
            Some mornings it loosens after 5 minutes. Other mornings it takes
            20. And on the worst days? It never fully goes away.
          </p>

          <HighlightBox variant="blue" data-id="element-83">
            <p className="font-medium m-0" data-id="element-84">
              "You feel older than you should—and it's only 7am."
            </p>
          </HighlightBox>

          <p data-id="element-85">
            You've tried rest. You've tried ice. You've worn braces. You've
            taken ibuprofen before bed.
          </p>
          <p data-id="element-86">But every single morning, it's the same story.</p>
          <p data-id="element-87">
            Here's the truth: Your ankle isn't just "stiff" because it's
            injured—it's stiff because of what happens to circulation and tissue
            temperature while you sleep.
          </p>
          <p className="font-bold text-gray-900" data-id="element-88">
            And once you understand these 5 reasons, the solution becomes
            obvious.
          </p>
        </section>

        <hr className="my-12 border-gray-200" data-id="element-89" />

        {/* Reason #1 */}
        <section className="space-y-6" data-id="element-90">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-91">
            Reason #1: Blood Flow Slows Dramatically Overnight
          </h2>
          <div className="rounded-xl overflow-hidden shadow-md my-6" data-id="element-92">
            <img src="https://cdn.magicpatterns.com/uploads/wjaTvbKVHoVxdpmsboGKLM/hf_20260202_151608_c33475e0-d9b0-4710-a292-e56dcaedfe9b.png" alt="Tendon inflammation and reduced blood flow" className="w-full h-auto object-cover" data-id="element-93" />
          </div>
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-94">
            When you're lying still for 7-9 hours, your heart rate drops, your
            blood pressure decreases, and circulation to your extremities
            (especially your ankles and feet) slows way down.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-95">
            For a healthy ankle, this isn't a big deal. But when you have
            chronic ankle pain, Achilles tendinopathy, or lingering stiffness,
            reduced circulation means reduced oxygen and nutrients getting to
            the tissues that need them most.
          </p>

          <div className="bg-gray-50 p-6 rounded-xl border border-gray-100" data-id="element-96">
            <h3 className="font-bold text-gray-900 mb-3" data-id="element-97">
              What This Feels Like:
            </h3>
            <ul className="space-y-2" data-id="element-98">
              <li className="flex items-start" data-id="element-99">
                <span className="mr-2 text-blue-500" data-id="element-100">•</span> Heavy, "dead
                weight" feeling in the ankle
              </li>
              <li className="flex items-start" data-id="element-101">
                <span className="mr-2 text-blue-500" data-id="element-102">•</span> Tightness that
                feels deep, like it's in the tendon itself
              </li>
              <li className="flex items-start" data-id="element-103">
                <span className="mr-2 text-blue-500" data-id="element-104">•</span> A sensation that
                your ankle "hasn't woken up yet"
              </li>
            </ul>
          </div>

          <HighlightBox variant="yellow" data-id="element-105">
            <p className="font-bold mb-2" data-id="element-106">The Missing Piece:</p>
            <p className="m-0" data-id="element-107">
              Your ankle needs a "circulation boost" BEFORE you take those first
              steps—not after you've already been limping around for 10 minutes.
            </p>
          </HighlightBox>
        </section>

        {/* Reason #2 */}
        <section className="space-y-6 mt-16" data-id="element-108">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-109">
            Reason #2: Your Ankle Temperature Drops (And Cold = Stiff)
          </h2>
          <div className="rounded-xl overflow-hidden shadow-md my-6" data-id="element-110">
            <img src="https://cdn.magicpatterns.com/uploads/jmTTUjAmgMvzgo2SB4hcaz/hf_20260202_150733_9c481453-412e-463d-982e-310f523c2e5a.png" alt="Ankle circulation and blood vessels anatomy" className="w-full h-auto object-cover" data-id="element-111" />
          </div>
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-112">
            Your body temperature naturally drops by 1-2 degrees while you
            sleep. That might not sound like much, but for an
            already-compromised ankle, even a slight drop in tissue temperature
            makes tendons, ligaments, and muscles significantly stiffer.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-113">
            <div className="bg-blue-50 p-4 rounded-lg text-center" data-id="element-114">
              <span className="block text-3xl mb-2" data-id="element-115">❄️</span>
              <p className="font-bold text-blue-900" data-id="element-116">Cold Tissue</p>
              <p className="text-sm text-blue-800" data-id="element-117">Rigid, brittle, painful</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center" data-id="element-118">
              <span className="block text-3xl mb-2" data-id="element-119">🔥</span>
              <p className="font-bold text-orange-900" data-id="element-120">Warm Tissue</p>
              <p className="text-sm text-orange-800" data-id="element-121">
                Elastic, flexible, mobile
              </p>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-122">
            You already know this works—that's why you instinctively reach for
            hot showers, heating pads, or warm baths when your ankle feels
            tight. Warmth works. It always has.
          </p>

          <HighlightBox variant="yellow" data-id="element-123">
            <p className="font-bold mb-2" data-id="element-124">The Missing Piece:</p>
            <p className="m-0" data-id="element-125">
              Most people heat their ankle AFTER it's already painful. What if
              you could keep it warm overnight or warm it up BEFORE your first
              steps?
            </p>
          </HighlightBox>
        </section>

        {/* Reason #3 */}
        <section className="space-y-6 mt-16" data-id="element-126">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-127">
            Reason #3: Pressure and Inflammation Build Around the Tendon
          </h2>
          <div className="rounded-xl overflow-hidden shadow-md my-6" data-id="element-128">
            <img src="https://cdn.magicpatterns.com/uploads/5auCSJyqqeerbjDz7Mpo9G/hf_20260202_134713_88f9bffb-5769-4d79-ae3c-a42696ae696d.png" alt="Swollen inflamed ankle showing pressure buildup" className="w-full h-auto object-cover" data-id="element-129" />
            <div className="bg-red-50 p-3 text-center" data-id="element-130">
              <p className="text-red-800 font-medium text-sm" data-id="element-131">
                Overnight fluid buildup causes morning stiffness and swelling
              </p>
            </div>
          </div>
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-132">
            When you're active during the day, muscle contractions help pump
            fluid away from your ankle. But when you're lying down for 8 hours?
            Gravity is no longer helping, and there's no muscle activity to keep
            fluid moving.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-133">
            Result: pressure and inflammation can build around the Achilles
            tendon and ankle joint, causing that tight, full feeling when you
            wake up.
          </p>

          <HighlightBox variant="yellow" data-id="element-134">
            <p className="font-bold mb-2" data-id="element-135">The Missing Piece:</p>
            <p className="m-0" data-id="element-136">
              Gentle, rhythmic compression while you're resting can help prevent
              that overnight pressure buildup around the Achilles—so you wake up
              less tight and stiff.
            </p>
          </HighlightBox>
        </section>

        {/* Reason #4 */}
        <section className="space-y-6 mt-16" data-id="element-137">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-138">
            Reason #4: Your Tendons Literally "Freeze" Without Movement
          </h2>

          <div className="rounded-xl overflow-hidden shadow-md my-6" data-id="element-139">
            <img src="https://cdn.magicpatterns.com/uploads/rGAsfT3BP8gCeXsQHff5TY/hf_20260202_151427_e350dd3c-871d-4478-a03d-97b623a630a1.png" alt="X-ray showing frozen ankle tendon and inflammation" className="w-full h-auto object-cover" data-id="element-140" />
          </div>

          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-141">
            Think of your Achilles tendon like a hinge on a door. If that hinge
            moves regularly, it stays loose and smooth. But if it sits still for
            hours? It gets stiff, creaky, and hard to move.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-142">
            When you sleep, your ankle barely moves. The tendon stays in one
            position for hours. And without movement, the tissues literally
            stiffen up.
          </p>

          <div className="bg-red-50 border-l-4 border-red-500 p-4 my-4" data-id="element-143">
            <p className="text-red-900 italic" data-id="element-144">
              "Movement = lubrication. Movement = circulation. Movement =
              flexibility."
            </p>
          </div>

          <HighlightBox variant="yellow" data-id="element-145">
            <p className="font-bold mb-2" data-id="element-146">The Missing Piece:</p>
            <p className="m-0" data-id="element-147">
              Gentle, rhythmic massage or vibration can simulate the effect of
              movement—helping tissues stay loose even while you're resting.
            </p>
          </HighlightBox>
        </section>

        {/* Reason #5 */}
        <section className="space-y-6 mt-16" data-id="element-148">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-149">
            Reason #5: Your Ankle Never Gets a True "Warm-Up"
          </h2>

          <div className="rounded-xl overflow-hidden shadow-md my-6" data-id="element-150">
            <img src="https://cdn.magicpatterns.com/uploads/1AYTBGHmo4cC6fgoAR8mhM/hf_20260202_160241_16f03ca7-f6b1-4ae4-9468-ac1fa86da6a6.png" alt="Cold ankle with ice packs showing lack of warm-up" className="w-full h-auto object-cover" data-id="element-151" />
          </div>

          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-152">
            Think about athletes. They don't just jump into sprints cold—they
            warm up first. Your ankle needs the same thing.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-153">
            But instead, you go from 8 hours of zero movement to immediately
            asking it to bear your full body weight. No wonder it hurts.
          </p>

          <HighlightBox variant="yellow" data-id="element-154">
            <p className="font-bold mb-2" data-id="element-155">The Missing Piece:</p>
            <p className="m-0" data-id="element-156">
              What if you could give your ankle that "warm-up" BEFORE your first
              steps—so you wake up loose instead of frozen?
            </p>
          </HighlightBox>
        </section>

        <CTAButton text="STOP THE MORNING PAIN" subtext="Try Barkvee Risk-Free for 60 Days" data-id="element-157" />

        {/* Analysis Section */}
        <section className="bg-gray-50 rounded-2xl p-8 my-16 border border-gray-100" data-id="element-158">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-159">
            Now It Makes Sense Why Nothing Has Worked
          </h2>

          <div className="space-y-6" data-id="element-160">
            <div data-id="element-161">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-162">
                Why traditional methods fail:
              </h3>
              <ul className="space-y-2" data-id="element-163">
                <CrossListItem data-id="element-164">
                  <strong data-id="element-165">Rest doesn't fix it:</strong> Complete rest makes
                  circulation slower and stiffness worse.
                </CrossListItem>
                <CrossListItem data-id="element-166">
                  <strong data-id="element-167">Ice increases rigidity:</strong> Cold makes tissues
                  stiffer, the opposite of what you need in the morning.
                </CrossListItem>
                <CrossListItem data-id="element-168">
                  <strong data-id="element-169">Braces just stabilize:</strong> They don't warm,
                  improve circulation, or loosen tight tissue.
                </CrossListItem>
                <CrossListItem data-id="element-170">
                  <strong data-id="element-171">Pills mask the pain:</strong> They don't address the
                  circulation shutdown or temperature drop.
                </CrossListItem>
              </ul>
            </div>

            <div className="pt-6 border-t border-gray-200" data-id="element-172">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-173">
                What your ankle actually needs:
              </h3>
              <ul className="space-y-2" data-id="element-174">
                <CheckListItem data-id="element-175">Better circulation overnight</CheckListItem>
                <CheckListItem data-id="element-176">Warmth to stay flexible</CheckListItem>
                <CheckListItem data-id="element-177">
                  Gentle compression to prevent pressure buildup
                </CheckListItem>
                <CheckListItem data-id="element-178">
                  Movement to avoid the "stiff hinge" effect
                </CheckListItem>
                <CheckListItem data-id="element-179">
                  A warm-up BEFORE you ask it to work
                </CheckListItem>
              </ul>
            </div>
          </div>
        </section>

        {/* Product Reveal */}
        <section className="my-16" data-id="element-180">
          <div className="text-center mb-10" data-id="element-181">
            <span className="text-green-600 font-bold tracking-wider uppercase text-sm" data-id="element-182">
              The Breakthrough
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mt-2 mb-4" data-id="element-183">
              The First Solution Designed for ALL 5 Overnight Problems
            </h2>
            <p className="text-xl text-gray-600" data-id="element-184">
              Meet <span className="font-bold text-gray-900" data-id="element-185">Barkvee</span>—a
              rechargeable heated ankle wrap that combines targeted warmth,
              gentle rhythmic compression, and massage.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-2xl mb-10 border border-gray-100" data-id="element-186">
            <img src="https://cdn.magicpatterns.com/uploads/3c3J8on3N34aL36HwHFexF/hf_20260202_150832_46a5b980-ccdc-4c03-b744-d66c5ee19071.png" alt="Barkvee Heated Ankle Wrap Product" className="w-full h-auto object-cover" data-id="element-187" />
          </div>

          <div className="space-y-8" data-id="element-188">
            <div className="flex flex-col sm:flex-row gap-6 items-start" data-id="element-189">
              <div className="bg-orange-100 p-3 rounded-full flex-shrink-0" data-id="element-190">
                <span className="text-2xl" data-id="element-191">🔥</span>
              </div>
              <div data-id="element-192">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-193">
                  1. Heat Loosens Tight Tissues
                </h3>
                <p className="text-gray-700" data-id="element-194">
                  Gentle, consistent warmth increases blood flow to the ankle
                  and Achilles, improving tissue elasticity. Tendons and
                  ligaments literally become more flexible when warm.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 items-start" data-id="element-195">
              <div className="bg-blue-100 p-3 rounded-full flex-shrink-0" data-id="element-196">
                <span className="text-2xl" data-id="element-197">🌊</span>
              </div>
              <div data-id="element-198">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-199">
                  2. Gentle Rhythmic Compression
                </h3>
                <p className="text-gray-700" data-id="element-200">
                  The massage function helps move pressure and fluid that builds
                  up around the tendon overnight. It supports your body's
                  natural venous and lymphatic return.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 items-start" data-id="element-201">
              <div className="bg-green-100 p-3 rounded-full flex-shrink-0" data-id="element-202">
                <span className="text-2xl" data-id="element-203">⚡</span>
              </div>
              <div data-id="element-204">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-205">
                  3. Wearable Convenience
                </h3>
                <p className="text-gray-700" data-id="element-206">
                  Use it for 15-20 minutes before bed, or first thing in the
                  morning while having coffee. It's like having a portable
                  warm-up session on demand.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof / Testimonials */}
        <section className="bg-gray-50 -mx-4 px-4 py-12 sm:rounded-3xl sm:mx-0 sm:px-8 my-16" data-id="element-207">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8" data-id="element-208">
            Real People, Real Relief
          </h2>

          <TestimonialCard quote="I've had chronic Achilles pain for 2 years. Every morning was torture. I tried everything. Then I found Barkvee. Now my first steps are actually comfortable. It's completely changed my mornings." author="Sarah M." role="Teacher, 52" imageUrl="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face" data-id="element-209" />

          <TestimonialCard quote="I was skeptical, but after the first night I could tell something was different. By week two, I wasn't even thinking about my ankle in the morning—I was just walking." author="David R." role="Warehouse Manager, 58" imageUrl="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face" data-id="element-210" />

          <TestimonialCard quote="Cold mornings used to destroy me. Now I use my Barkvee for 20 minutes while I have my coffee, and by the time I'm ready to leave, my ankle is loose and warm." author="Jennifer L." role="Nurse, 47" imageUrl="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face" data-id="element-211" />

          <div className="mt-8 text-center" data-id="element-212">
            <p className="text-gray-400 text-sm font-medium tracking-wide" data-id="element-213">
              TRUSTED BY 12,000+ CUSTOMERS
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="my-16" data-id="element-214">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-215">
            "But Will This Actually Work for Me?"
          </h2>

          <div className="space-y-2" data-id="element-216">
            <FAQItem question="Is heat really safe for ankle pain?" answer="Yes—heat is recommended for chronic stiffness and circulation issues. It's cold/ice that can increase stiffness when inflammation isn't acute. Clinical guidance specifically recommends warmth for morning stiffness." data-id="element-217" />
            <FAQItem question="Will it be too tight or cut off circulation?" answer="Barkvee uses gentle, rhythmic compression—not aggressive tightening. You control the fit, and the heat actively encourages blood flow (the opposite of cutting it off)." data-id="element-218" />
            <FAQItem question="How long before I feel relief?" answer="Most people notice their ankle feels looser and more comfortable within the first 15-20 minute session. Consistent daily use compounds that relief." data-id="element-219" />
            <FAQItem question="Can I use this if I have plantar fasciitis too?" answer="Yes—many users with both Achilles and plantar fasciitis issues find relief because both conditions benefit from improved circulation and warmth." data-id="element-220" />
            <FAQItem question="Is it bulky? Can I actually wear it comfortably?" answer="Barkvee is designed to be worn during rest. It's lightweight, adjustable, and comfortable enough to wear while sitting, relaxing, or sleeping." data-id="element-221" />
            <FAQItem question="What if it doesn't work for me?" answer="Every Barkvee comes with a 60-day money-back guarantee. Try it for two full months. If your mornings aren't significantly better, return it for a full refund." data-id="element-222" />
          </div>
        </section>

        {/* Urgency & Final CTA */}
        <section className="text-center my-16" data-id="element-223">
          <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-224">
            Tomorrow Morning Is Coming Either Way
          </h2>
          <p className="text-xl text-gray-700 mb-8 leading-relaxed" data-id="element-225">
            The only question is: Will your ankle wake up stiff, frozen, and
            painful again? Or will you wake up loose, warm, and ready to move?
          </p>

          <div className="bg-green-50 border border-green-200 rounded-2xl p-8 mb-8" data-id="element-226">
            <h3 className="font-bold text-xl text-green-900 mb-4" data-id="element-227">
              What You Get With Every Barkvee:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left" data-id="element-228">
              <div className="flex items-center" data-id="element-229">
                <Check className="w-5 h-5 text-green-600 mr-2" data-id="element-230" /> Rechargeable
                heated ankle wrap
              </div>
              <div className="flex items-center" data-id="element-231">
                <Check className="w-5 h-5 text-green-600 mr-2" data-id="element-232" /> Adjustable fit
                for most sizes
              </div>
              <div className="flex items-center" data-id="element-233">
                <Check className="w-5 h-5 text-green-600 mr-2" data-id="element-234" /> Multiple heat
                settings
              </div>
              <div className="flex items-center" data-id="element-235">
                <Check className="w-5 h-5 text-green-600 mr-2" data-id="element-236" /> Gentle
                rhythmic massage
              </div>
              <div className="flex items-center" data-id="element-237">
                <Check className="w-5 h-5 text-green-600 mr-2" data-id="element-238" /> 60-day
                money-back guarantee
              </div>
              <div className="flex items-center" data-id="element-239">
                <Check className="w-5 h-5 text-green-600 mr-2" data-id="element-240" /> 12-month
                warranty
              </div>
            </div>
          </div>

          <CTAButton text="GET BARKVEE NOW" subtext="Join 12,000+ people who no longer dread their first steps" data-id="element-241" />
        </section>

        {/* Footer / Disclaimer */}
        <footer className="border-t border-gray-200 pt-8 mt-16 text-center text-gray-500 text-sm" data-id="element-242">
          <div className="flex items-center justify-center mb-4 text-gray-400" data-id="element-243">
            <AlertCircle className="w-4 h-4 mr-2" data-id="element-244" />
            <span className="font-bold" data-id="element-245">Medical Disclaimer</span>
          </div>
          <p className="max-w-2xl mx-auto leading-relaxed" data-id="element-246">
            Barkvee is designed for comfort and wellness support. It is not
            intended to diagnose, treat, cure, or prevent any medical condition.
            If you have severe or persistent ankle pain, please consult with a
            healthcare professional. Do not use if you have poor circulation,
            diabetes, or skin sensitivity without consulting your doctor first.
          </p>
          <div className="mt-8 mb-4 space-x-4" data-id="element-247">
            <a href="#" className="hover:underline" data-id="element-248">
              Privacy Policy
            </a>
            <a href="#" className="hover:underline" data-id="element-249">
              Terms of Service
            </a>
            <a href="#" className="hover:underline" data-id="element-250">
              Contact Us
            </a>
          </div>
          <p data-id="element-251">© {new Date().getFullYear()} Barkvee. All rights reserved.</p>
        </footer>
      </main>
    </div>;
}